import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import type {
  ClaudeChannelApproval,
  ClaudeChannelConversation,
  ClaudeChannelMessage,
} from "common";

const BASE = "/api/v1/claude-channel";

export const claudeChannelKeys = {
  conversations: ["claude-channel", "conversations"] as const,
  approvals: (limit: number) => ["claude-channel", "approvals", limit] as const,
};

export const fetchClaudeChannelConversations = async (): Promise<
  ClaudeChannelConversation[]
> =>
  (await axios.get<ClaudeChannelConversation[]>(`${BASE}/conversations`)).data;

export const fetchClaudeChannelMessages = async (
  conversationId: number
): Promise<ClaudeChannelMessage[]> =>
  (
    await axios.get<ClaudeChannelMessage[]>(
      `${BASE}/conversations/${conversationId}/messages`
    )
  ).data;

export const useClaudeChannelConversations = (
  enabled = true
): UseQueryResult<ClaudeChannelConversation[]> =>
  useQuery({
    queryKey: claudeChannelKeys.conversations,
    queryFn: fetchClaudeChannelConversations,
    enabled,
  });

export const useDeleteClaudeChannelConversation = (): UseMutationResult<
  void,
  Error,
  number
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: number) => {
      await axios.delete(`${BASE}/conversations/${conversationId}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: claudeChannelKeys.conversations,
      }),
  });
};

/** The caller's own approval history. There is no route that reads anyone else's. */
export const useClaudeChannelApprovals = (
  limit = 100,
  enabled = true
): UseQueryResult<ClaudeChannelApproval[]> =>
  useQuery({
    queryKey: claudeChannelKeys.approvals(limit),
    queryFn: async () =>
      (
        await axios.get<ClaudeChannelApproval[]>(`${BASE}/approvals`, {
          params: { limit },
        })
      ).data,
    enabled,
  });
